import React, { useEffect, useState } from 'react';
import classes from '../departments.module.scss';
import { createCn } from '../../../helpers/utils';
import { AdminUserItem, DepartmentItem } from '../../../services/api.service';

const cn = createCn('departments', classes);

const DEFAULT_PRIORITY = 50;

function effectivePriority(
  user: AdminUserItem,
  departmentsById: Map<number, DepartmentItem>,
): { value: number; label: string } {
  if (user.priority_override !== null && user.priority_override !== undefined) {
    return { value: user.priority_override, label: 'личный' };
  }
  const department = user.department_id != null ? departmentsById.get(user.department_id) : undefined;
  if (department) {
    return { value: department.priority, label: 'отдел' };
  }
  return { value: DEFAULT_PRIORITY, label: 'по умолчанию' };
}

interface UserPriorityRowProps {
  user: AdminUserItem;
  departments: DepartmentItem[];
  departmentsById: Map<number, DepartmentItem>;
  savingDepartment: boolean;
  savingPriority: boolean;
  onChangeDepartment: (departmentId: number | null) => void;
  onChangePriority: (priority: number | null) => void;
}

const UserPriorityRow = ({
  user,
  departments,
  departmentsById,
  savingDepartment,
  savingPriority,
  onChangeDepartment,
  onChangePriority,
}: UserPriorityRowProps) => {
  const [priorityDraft, setPriorityDraft] = useState(
    user.priority_override != null ? String(user.priority_override) : '',
  );

  useEffect(() => {
    setPriorityDraft(user.priority_override != null ? String(user.priority_override) : '');
  }, [user.priority_override]);

  const commitPriority = () => {
    const trimmed = priorityDraft.trim();
    if (trimmed === '') {
      if (user.priority_override != null) onChangePriority(null);
      return;
    }
    const parsed = Math.round(Number(trimmed));
    if (Number.isNaN(parsed)) {
      setPriorityDraft(user.priority_override != null ? String(user.priority_override) : '');
      return;
    }
    const clamped = Math.min(100, Math.max(0, parsed));
    setPriorityDraft(String(clamped));
    if (clamped !== user.priority_override) onChangePriority(clamped);
  };

  const effective = effectivePriority(user, departmentsById);

  return (
    <div className={cn('user-row')}>
      <div className={cn('user-identity')}>
        <div className={cn('user-name')} title={user.full_name || user.email}>
          {user.full_name || '—'}
        </div>
        <div className={cn('user-email')} title={user.email}>{user.email}</div>
      </div>

      <select
        className={cn('user-dept-select')}
        value={user.department_id != null ? String(user.department_id) : ''}
        disabled={savingDepartment}
        onChange={(e) => onChangeDepartment(e.target.value === '' ? null : Number(e.target.value))}
        aria-label={`Отдел пользователя ${user.email}`}
      >
        <option value="">— без отдела —</option>
        {departments.map((department) => (
          <option key={department.id} value={String(department.id)}>
            {department.name}
          </option>
        ))}
      </select>

      <input
        type="number"
        className={cn('user-priority-input')}
        min={0}
        max={100}
        placeholder="по отделу"
        value={priorityDraft}
        disabled={savingPriority}
        onChange={(e) => setPriorityDraft(e.target.value)}
        onBlur={commitPriority}
        onKeyDown={(e) => {
          if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
        }}
        aria-label={`Личный приоритет пользователя ${user.email}`}
      />

      <div className={cn('user-effective')}>
        = {effective.value} <span className={cn('user-effective-source')}>({effective.label})</span>
      </div>
    </div>
  );
};

interface UserPriorityListProps {
  users: AdminUserItem[];
  departments: DepartmentItem[];
  savingUserId: number | null;
  savingField: 'department' | 'priority' | null;
  onChangeDepartment: (userId: number, departmentId: number | null) => void;
  onChangePriority: (userId: number, priority: number | null) => void;
}

const UserPriorityList = ({
  users,
  departments,
  savingUserId,
  savingField,
  onChangeDepartment,
  onChangePriority,
}: UserPriorityListProps) => {
  const departmentsById = new Map(departments.map((department) => [department.id, department]));

  return (
    <div className={cn('section')}>
      <div className={cn('section-title')}>Пользователи ({users.length})</div>

      {users.length === 0 ? (
        <div className={cn('empty')}>Пользователей пока нет</div>
      ) : (
        <div className={cn('user-list')}>
          <div className={cn('user-row', { header: true })}>
            <div>Пользователь</div>
            <div>Отдел</div>
            <div>Личный приоритет</div>
            <div>Итог</div>
          </div>

          {users.map((user) => (
            <UserPriorityRow
              key={user.id}
              user={user}
              departments={departments}
              departmentsById={departmentsById}
              savingDepartment={savingUserId === user.id && savingField === 'department'}
              savingPriority={savingUserId === user.id && savingField === 'priority'}
              onChangeDepartment={(departmentId) => onChangeDepartment(user.id, departmentId)}
              onChangePriority={(priority) => onChangePriority(user.id, priority)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default UserPriorityList;
